/**
 * Leads API - Now uses Supabase
 *
 * This file re-exports from the Supabase leads API for backwards compatibility.
 */

import { leadsApi as supabaseLeadsApi } from './supabase/leads';
import type { LeadStatus } from './types';
import type { Customer } from 'src/types/customer';

// Re-export the supabase leads API
export const leadsApi = {
  // Get all leads with optional filters
  async getAll(params?: {
    leadStatus?: LeadStatus;
    search?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
  }) {
    const response = await supabaseLeadsApi.getAll({
      filters: {
        leadStatus: params?.leadStatus,
        query: params?.search,
      },
      page: (params?.page || 1) - 1, // Convert 1-based to 0-based
      rowsPerPage: params?.pageSize || 25,
      sortBy: params?.sortBy,
      sortDir: params?.sortDir,
    });

    // Return in format expected by existing code
    return {
      data: response.data,
      meta: {
        pagination: {
          total: response.count,
          page: params?.page || 1,
          pageSize: params?.pageSize || 25,
        },
      },
    };
  },

  // Get a single lead by ID
  async getById(id: number | string) {
    const data = await supabaseLeadsApi.getById(id.toString());
    return { data, meta: {} };
  },

  // Create a new lead
  async create(data: any) {
    const result = await supabaseLeadsApi.create(data);
    return { data: result };
  },

  // Update an existing lead
  async update(id: number | string, data: any) {
    // console.log('leadsApi.update called with id:', id);
    // console.log('Fields being updated:', Object.keys(data));
    const result = await supabaseLeadsApi.update(id.toString(), data);
    return { data: result };
  },

  // Delete a lead
  async delete(id: number | string) {
    await supabaseLeadsApi.delete(id.toString());
    return { data: null };
  },

  // Get leads by status
  async getByStatus(leadStatus: LeadStatus, page = 1, pageSize = 25) {
    return this.getAll({ leadStatus, page, pageSize });
  },

  // Search leads
  async search(query: string, page = 1, pageSize = 25) {
    return this.getAll({ search: query, page, pageSize });
  },

  // Update lead status
  async updateStatus(id: number | string, leadStatus: LeadStatus) {
    return this.update(id, { leadStatus });
  },

  // Log a contact (update lastContactDate)
  async logContact(id: number | string, nextFollowUpDate?: string, notes?: string) {
    const data: any = {
      lastContactDate: new Date().toISOString(),
    };
    if (nextFollowUpDate) {
      data.nextFollowUpDate = nextFollowUpDate;
    }
    if (notes) {
      data.followUpNotes = notes;
    }
    return this.update(id, data);
  },
};

// Helper function to format lead display name
export function getLeadDisplayName(lead: Customer | any): string {
  if (lead.name) {
    return lead.name;
  }
  const firstName = lead.firstName || lead.first_name || '';
  const lastName = lead.lastName || lead.last_name || '';
  return `${firstName} ${lastName}`.trim();
}

// Helper function to format lead full address
export function getLeadFullAddress(lead: Customer | any): string {
  const parts = [
    lead.address1 || lead.address,
    lead.city,
    lead.state,
    lead.zipCode || lead.zip_code,
  ].filter(Boolean);
  return parts.join(', ');
}

// Helper function to format project address
export function getProjectFullAddress(lead: Customer | any): string {
  const parts = [
    lead.projectAddress || lead.project_address,
    lead.projectCity || lead.project_city,
    lead.projectState || lead.project_state,
    lead.projectZip || lead.project_zip,
  ].filter(Boolean);
  return parts.join(', ');
}
